import InfoIcon from '@mui/icons-material/Info';
import { Button, CardMedia, Checkbox, CircularProgress, FormControlLabel, MenuItem, SelectChangeEvent, Stack, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import * as MaterialHelper from '../../api/material/materialHelper';
import { physicalData, relativeDensity, rockD50, shieldsParameter, thickness } from '../../conversion/physicalData';
import DropDown from '../../shared/component/DropDown';
import InputText from '../../shared/component/InputText';
import { EActiveDoubleLayer, EMaterialCategory, ETypeOfQuay, EUnitOfMeasure } from '../../shared/enum';
import { IMaterial, IPhysicalData } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { cleanAlert } from '../../store/alert/alertSlice';
import { cleanEngine } from '../../store/engine/engineSlice';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { cleanMap } from '../../store/map/mapSlice';
import { removeMaterialDoubleLayer, selectProject, setGeometry, setMaterial, setMaterialDoubleLayer } from '../../store/project/projectSlice';
import { cleanResult } from '../../store/result/resultSlice';
import DoubleLayerMaterial from './DoubleLayerMaterial.png';
import { Context } from './Index';

interface IMaccaferriMaterialProps {}

const MaccaferriMaterial = (props: IMaccaferriMaterialProps) => {
    const context = React.useContext(Context);

    const project = useAppSelector(selectProject);

    const getAllRockD50ValueByThickness = (thickness: number): number[] => {
        const filterByThickness = context!.baseMaterial.physicalData.filter(x => x.thickness === thickness || x.thicknessIn === thickness);
        const rockD50Values = filterByThickness.map(x => (project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric ? x.rockD50 : x.rockD50in));
        const uniqueRockD50Values = [...new Set(rockD50Values)];
        return uniqueRockD50Values;
    };

    const getAllThicknessValue = () => {
        const thicknessValues = context?.baseMaterial.physicalData.map(x => (project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric ? x.thickness : x.thicknessIn));
        const uniqueThicknessValues = [...new Set(thicknessValues)];
        return uniqueThicknessValues || [];
    };

    const getInitialThickness = () => {
        let allThicknessValue = getAllThicknessValue();
        let isAvailable = allThicknessValue.some(x => x == project.data?.material?.physicalData[0].thickness || x == project.data?.material?.physicalData[0].thicknessIn);
        if (isAvailable) {
            return project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric ? project.data!.material!.physicalData[0].thickness : project.data!.material!.physicalData[0].thicknessIn;
        } else {
            return allThicknessValue[0];
        }
    };

    const getInitialRockD50 = () => {
        let thickness = getInitialThickness();
        let allRockD50ValueByThickness = getAllRockD50ValueByThickness(thickness);
        let isAvailable = allRockD50ValueByThickness.some(x => x == project.data?.material?.physicalData[0].rockD50 || x == project.data?.material?.physicalData[0].rockD50in);
        if (isAvailable) {
            return project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric ? project.data!.material!.physicalData[0].rockD50 : project.data!.material!.physicalData[0].rockD50in;
        } else {
            return allRockD50ValueByThickness[0];
        }
    };

    const [thicknessOpt, setThicknessOpt] = React.useState<number[]>(getAllThicknessValue());
    const [thicknessSelected, setThicknessSelected] = React.useState<number>(getInitialThickness());
    const [rockD50Opt, setRockD50Opt] = React.useState<number[]>(getAllRockD50ValueByThickness(thicknessSelected));
    const [rockD50Selected, setRockD50Selected] = React.useState<number>(getInitialRockD50());
    const [physicalDataState, setPhysicalDataState] = React.useState<IPhysicalData>(
        project.data?.material?.baseMaterialId == context!.baseMaterial!.id ? project!.data!.material!.physicalData[0] : context!.baseMaterial!.physicalData[0],
    );

    const { t } = useTranslation();

    const handleChangeThickness = (e: SelectChangeEvent<unknown>) => {
        let thickness = e.target.value as number;
        setThicknessSelected(thickness);

        let rockD50Opt = getAllRockD50ValueByThickness(thickness);
        setRockD50Selected(rockD50Opt[0]);
        setRockD50Opt(rockD50Opt);

        let physicalData = context?.baseMaterial.physicalData.find(x => (x.thickness == thickness || x.thicknessIn == thickness) && (x.rockD50 == rockD50Opt[0] || x.rockD50in == rockD50Opt[0]));
        setPhysicalDataState(physicalData!);
    };

    const handleChangeRockD50 = (e: SelectChangeEvent<unknown>) => {
        let rockD50 = e.target.value as number;
        setRockD50Selected(rockD50);

        let physicalData = context?.baseMaterial.physicalData.find(x => (x.thickness == thicknessSelected || x.thicknessIn == thicknessSelected) && (x.rockD50 == rockD50 || x.rockD50in == rockD50));
        setPhysicalDataState(physicalData!);
    };

    const handleChangePhysicalDataMaterial = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        setPhysicalDataState({ ...physicalDataState, [name]: value });
    };

    const getAllMaterialCategoryThicknessValue = (index: number) => {
        const thicknessValues = materialByCategory![index].physicalData.map(x => (project.data!.project!.unitOfMeasure === EUnitOfMeasure.Metric ? x.thickness : x.thicknessIn));
        const uniqueThicknessValues = [...new Set(thicknessValues)];
        return uniqueThicknessValues || [];
    };

    const getInitialThicknessDoubleLayer = (index: number) => {
        let allThicknessValue = getAllMaterialCategoryThicknessValue(index);
        let isAvailable = allThicknessValue.some(x => x == project.data?.doubleLayerMaterial?.physicalData[0]?.thickness || x == project.data?.doubleLayerMaterial?.physicalData[0]?.thicknessIn);
        if (isAvailable) {
            return project.data!.doubleLayerMaterial!.physicalData[0].thickness;
        } else {
            return allThicknessValue[0];
        }
    };

    //Double layer
    const dispatch = useAppDispatch();

    const [isLoading, setIsLoading] = React.useState(false);
    const [thicknessDoubleLayerOpt, setThicknessDoubleLayerOpt] = React.useState<number[] | null>();
    const [thicknessDoubleLayerSelected, setThicknessDoubleLayerSelected] = React.useState<number | null>();
    const [materialByCategory, setMaterialByCategory] = React.useState<IMaterial[] | null>();
    const [index, setIndex] = React.useState<number>(0);
    const [activeDoubleLayer, setActiveDoubleLayer] = React.useState<EActiveDoubleLayer>(
        project.data?.geometry?.doubleLayerNearToTheQuayWall || project.data?.doubleLayerMaterial ? EActiveDoubleLayer.Yes : EActiveDoubleLayer.No,
    );

    React.useEffect(() => {
        const fetchMaterialByCategory = async () => {
            setIsLoading(true);
            let res = await MaterialHelper.getMaterialByCategoryAsync(EMaterialCategory.RenoMac | EMaterialCategory.CubiMac);
            setMaterialByCategory(res.data);
            setIsLoading(false);
        };
        fetchMaterialByCategory();
    }, []);

    React.useEffect(() => {
        if (materialByCategory?.length! > 0) {
            let index = 0;
            if (project.data?.geometry?.doubleLayerNearToTheQuayWall || project.data?.doubleLayerMaterial) {
                index = materialByCategory!.findIndex(x => x.id == project.data?.doubleLayerMaterial?.baseMaterialId);
            }
            setIndex(index!);

            let thicknessOpt = getAllMaterialCategoryThicknessValue(index!);
            setThicknessDoubleLayerOpt(thicknessOpt);

            let thickness = getInitialThicknessDoubleLayer(index!);
            setThicknessDoubleLayerSelected(thickness);
        }
    }, [materialByCategory]);

    React.useEffect(() => {
        if (materialByCategory?.length! > 0) {
            setIndex(index!);

            let thicknessOpt = getAllMaterialCategoryThicknessValue(index!);
            setThicknessDoubleLayerOpt(thicknessOpt);

            let thickness = getInitialThicknessDoubleLayer(index!);
            setThicknessDoubleLayerSelected(thickness);
        }
    }, [index]);

    const handleChangeThicknessDoubleLayer = (e: SelectChangeEvent<unknown>) => setThicknessDoubleLayerSelected(e.target.value as number);

    const handleClickApply = () => {
        dispatch(cleanResult());
        dispatch(cleanEngine());
        dispatch(cleanMap());
        dispatch(cleanAlert());
        dispatch(
            setMaterial({
                ...context!.baseMaterial,
                physicalData: [{ ...physicalDataState, widthOfTheProtectionW: project.data?.material?.physicalData[0].widthOfTheProtectionW! }],
                id: project.data!.material!.id!,
                baseMaterialId: context!.baseMaterial!.id!,
                isBaseMaterial: false,
            }),
        );
        if (activeDoubleLayer) {
            let material: IMaterial = {
                ...materialByCategory![index],
                physicalData: [materialByCategory![index].physicalData.filter(x => x.thickness == thicknessDoubleLayerSelected || x.thicknessIn == thicknessDoubleLayerSelected)[0]],
                id: project.data?.doubleLayerMaterial?.id ?? materialByCategory![index].id,
                baseMaterialId: materialByCategory![index].id,
                isBaseMaterial: project.data?.doubleLayerMaterial?.isBaseMaterial ?? materialByCategory![index].isBaseMaterial,
            };
            dispatch(setGeometry({ ...project.data!.geometry!, doubleLayerNearToTheQuayWall: true }));
            dispatch(setMaterialDoubleLayer(material));
        } else {
            dispatch(removeMaterialDoubleLayer());
            dispatch(setGeometry({ ...project.data!.geometry!, doubleLayerNearToTheQuayWall: false }));
        }
        Swal.fire({
            title: t('addedMaterial'),
            icon: 'success',
            toast: true,
            position: 'top',
            showConfirmButton: false,
            timer: 2000,
        });
    };

    return (
        <Stack direction={'column'}>
            {isLoading && <CircularProgress />}
            {!isLoading && materialByCategory?.length! > 0 && thicknessDoubleLayerOpt?.length! > 0 && (
                <>
                    <Stack direction={'row'} spacing={3}>
                        <FormControlLabel control={<Checkbox checked={context!.baseMaterial.geotextileFilter} disabled />} label={t('geotextileFilter')} />
                        <FormControlLabel control={<Checkbox checked={context!.baseMaterial.xties} disabled />} label={t('xTies')} />
                    </Stack>
                    <Stack direction={'row'} spacing={2}>
                        <DropDown
                            className='btn'
                            inputLabel={t(thickness, {
                                unitOfMeasure: physicalData['thickness'].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                            })}
                            labelId={`${thickness}-label`}
                            id={thickness}
                            value={thicknessSelected}
                            onChange={handleChangeThickness}>
                            {thicknessOpt.map(x => (
                                <MenuItem value={x}>{x}</MenuItem>
                            ))}
                        </DropDown>
                        <DropDown
                            className='btn'
                            inputLabel={t(rockD50, {
                                unitOfMeasure: physicalData['rockD50'].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                            })}
                            labelId={`${rockD50}-label`}
                            value={rockD50Selected}
                            id={rockD50}
                            onChange={handleChangeRockD50}>
                            {rockD50Opt.map(x => (
                                <MenuItem value={x}>{context?.baseMaterial.category == EMaterialCategory.CubiMac ? `>=${x}` : x}</MenuItem>
                            ))}
                        </DropDown>
                        <InputText
                            className='btn'
                            id={relativeDensity}
                            customValue={physicalDataState.relativeDensity}
                            isNumber
                            inputLabel={t(relativeDensity)}
                            name={relativeDensity}
                            customOnChange={handleChangePhysicalDataMaterial}
                        />
                    </Stack>
                    <InputText
                        disabled
                        id={shieldsParameter}
                        textTooltip={t('shieldsParameterInfo')}
                        customValue={physicalDataState.shieldsParameter}
                        isNumber
                        inputLabel={t(shieldsParameter)}
                        name={shieldsParameter}
                        customOnChange={handleChangePhysicalDataMaterial}
                    />
                    {project.data?.geometry?.typeOfQuay === ETypeOfQuay.Vertical && (
                        <>
                            <Typography style={{ fontSize: '1rem' }}>
                                <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
                                    {t('doubleLayerAtTransition')}
                                    <Tooltip title={<CardMedia sx={{ height: 200, width: 200 }} image={DoubleLayerMaterial} title='Pilarczyk Formula' />}>
                                        <InfoIcon style={{ fill: variable.green }} fontSize='small' />
                                    </Tooltip>
                                </Stack>
                            </Typography>
                            <Stack direction={'row'} spacing={2}>
                                <DropDown
                                    className='btn'
                                    inputLabel={t('active')}
                                    value={activeDoubleLayer}
                                    labelId={'active-double-layer-label'}
                                    id={'active-double-layer'}
                                    onChange={e => setActiveDoubleLayer(e.target.value as EActiveDoubleLayer)}>
                                    <MenuItem value={EActiveDoubleLayer.No}>{t('no')}</MenuItem>
                                    <MenuItem value={EActiveDoubleLayer.Yes}>{t('yes')}</MenuItem>
                                </DropDown>
                                <DropDown
                                    className='btn'
                                    disabled={!activeDoubleLayer}
                                    value={index}
                                    inputLabel={t('material')}
                                    labelId={'material-label'}
                                    id={'material'}
                                    onChange={e => setIndex(e.target.value as number)}>
                                    {materialByCategory!.map((x, i) => (
                                        <MenuItem value={i}>{x.translations![0].name}</MenuItem>
                                    ))}
                                </DropDown>
                                <DropDown
                                    className='btn'
                                    disabled={!activeDoubleLayer}
                                    inputLabel={t(thickness, {
                                        unitOfMeasure: physicalData['thickness'].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                    })}
                                    labelId={'thickness-double-layer-label'}
                                    id={'thickness-double-layer'}
                                    value={thicknessDoubleLayerSelected}
                                    onChange={handleChangeThicknessDoubleLayer}>
                                    {thicknessDoubleLayerOpt!.map(x => (
                                        <MenuItem value={x}>{x}</MenuItem>
                                    ))}
                                </DropDown>
                            </Stack>
                        </>
                    )}
                    <Stack direction={'row'} spacing={5} justifyContent={'flex-end'}>
                        <Button className='btn' variant='white' onClick={() => context!.handleClose()}>
                            {t('close')}
                        </Button>
                        <Button className='btn' variant='orange' onClick={handleClickApply}>
                            {t('apply')}
                        </Button>
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default MaccaferriMaterial;
